/* Rendering a legal document from blocks — STORY-048.
 *
 * Every block becomes a real element. Nothing here touches
 * `dangerouslySetInnerHTML`, which `frontend-react.md` bans outright — and
 * that ban is the reason the API sends blocks rather than HTML in the first
 * place, so this file is where that decision pays for itself.
 *
 * The other reason it is worth having: the same block union will render on
 * native, where there is no HTML at all. A heading is a heading whether it
 * becomes an `<h2>` or a `<Text>`.
 */

import type { LegalBlock, LegalInline } from '@eventuley/contracts';

/**
 * One run of text.
 *
 * A link opens in a new tab with `noopener`: these documents cite regulators
 * and processors, and a reader following a citation should not lose the
 * contract they were part-way through.
 */
function Run({ run }: { run: LegalInline }) {
  if (run.href !== undefined) {
    return (
      <a href={run.href} target="_blank" rel="noopener noreferrer">
        {run.text}
      </a>
    );
  }
  if (run.code === true) return <code>{run.text}</code>;
  if (run.bold === true) return <strong>{run.text}</strong>;
  if (run.italic === true) return <em>{run.text}</em>;
  return <>{run.text}</>;
}

const Runs = ({ content }: { content: readonly LegalInline[] }) => (
  <>
    {content.map((run, index) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: runs are positional and never reordered
      <Run key={index} run={run} />
    ))}
  </>
);

function Block({ block }: { block: LegalBlock }) {
  switch (block.kind) {
    case 'heading': {
      /* Levels come from the document and are already 1-4. The document's own
       * h1 is its title, which the page renders separately, so these start at
       * h2 to keep one h1 per page. */
      const Tag = (['h2', 'h2', 'h3', 'h4'] as const)[block.level - 1] ?? 'h4';
      return (
        <Tag>
          <Runs content={block.content} />
        </Tag>
      );
    }

    case 'paragraph':
      return (
        <p>
          <Runs content={block.content} />
        </p>
      );

    case 'quote':
      return (
        <blockquote>
          {block.content.map((line, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional, never reordered
            <p key={index}>
              <Runs content={line} />
            </p>
          ))}
        </blockquote>
      );

    case 'list': {
      const items = block.items.map((item, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positional, never reordered
        <li key={index}>
          <Runs content={item} />
        </li>
      ));
      return block.ordered ? <ol>{items}</ol> : <ul>{items}</ul>;
    }

    case 'table':
      /* Wrapped, because `design.md` requires wide content to scroll inside its
       * own container rather than making the page scroll sideways — and these
       * documents contain some genuinely wide tables. */
      return (
        <div className="legal__table-scroll">
          <table>
            <thead>
              <tr>
                {block.headers.map((cell, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional, never reordered
                  <th key={index} scope="col">
                    <Runs content={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional, never reordered
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: positional, never reordered
                    <td key={cellIndex}>
                      <Runs content={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'rule':
      return <hr />;
  }
}

export function DocumentBlocks({ blocks }: { blocks: readonly LegalBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positional, never reordered
        <Block key={index} block={block} />
      ))}
    </>
  );
}
